import React, { useCallback, useEffect, useRef, useState } from 'react';
import { KpPanel, KpPanelTabs } from '../ui/panel';
import { useToast } from '../ui/ToastProvider';
import { kpConfirm } from '../ui/dialog';
import ForslagFormular from './ForslagFormular';
import MinaArendenLista, { IkonPil } from './MinaArendenLista';
import MinaArendenDetalj from './MinaArendenDetalj';
import { skapaForslag, laddaUppFil, hamtaMinaArenden, hamtaMittArende } from './forslagApi';
import MejlVal from './MejlVal';
import { TYPNAMN, arAktivt, datum, markeraSett, nyStatus } from './minaArendenStatus';
import { OPPNA_FORSLAG, OPPNA_FORSLAG_GAMMAL } from './oppnaForslag';
import { byggAnrop, sorteraFiler, typFor, TYPER, KALLOR } from './forslagRegler';
import { useIssueContext } from '../../modules/Feedback/useIssueContext';
import './Forslag.css';

/**
 * ForslagPanel – den enda vägen in för buggar, förbättringar, nya funktioner och frågor (BL-169).
 * Monteras en gång i Layout för alla inloggade. Öppnas med oppnaForslag({ kalla, typ, modul, rubrik })
 * från sidfoten, hörnknappen och På gång. Högerpanel KpPanel md i formulärläge (DS Del Y, HP-43).
 * Allt sparas i feedback_items via POST /api/v1/feedback, med ingången i context.kalla.
 *
 * BL-377: fliken Mina ärenden visar den inloggades egna ärenden och status
 * (GET /api/v1/feedback/mina). oppnaForslag({ vy: 'mina', arende: 'BUG-2026-0012' }) öppnar
 * fliken, och ett ärende direkt. Formulärläge (gråtoning) bara på fliken Nytt ärende.
 */
const REF = /^[A-Z]{3,4}-\d{4}-\d{4}$/;
const TOMT = { typKey: 'bug', rubrik: '', beskrivning: '', modul: '', allvarlighet: '', paverkan: '' };

let versionCache = null;
function hamtaAppVersion() {
  const env = typeof process !== 'undefined' && process.env && process.env.REACT_APP_VERSION;
  if (env) return Promise.resolve(env);
  if (versionCache) return versionCache;
  versionCache = fetch('/version.json')
    .then((r) => (r.ok ? r.json() : null))
    .then((d) => (d && d.app && d.app.version) || null)
    .catch(() => null);
  return versionCache;
}

function forvalTillVarden(f) {
  const typ = TYPER.some((t) => t.key === f.typ) ? f.typ : (f.kalla === 'pa_gang' ? 'improvement' : 'bug');
  return { ...TOMT, typKey: typ, modul: f.modul || '', rubrik: (f.rubrik || '').slice(0, 90) };
}

export default function ForslagPanel() {
  const [open, setOpen] = useState(false);
  const [kalla, setKalla] = useState('fab');
  const [varden, setVarden] = useState(TOMT);
  const [filer, setFiler] = useState([]);
  const [filfel, setFilfel] = useState([]);
  const [fel, setFel] = useState({});
  const [skickar, setSkickar] = useState(false);
  const [skickfel, setSkickfel] = useState('');
  const [klar, setKlar] = useState(null);
  const getContext = useIssueContext();
  const { push } = useToast();
  const autoModulRef = useRef('');
  const skickarRef = useRef(false);
  const [flik, setFlik] = useState('nytt');
  const [rader, setRader] = useState(null);
  const [listfel, setListfel] = useState('');
  const [nya, setNya] = useState(() => new Set());
  const [valt, setValt] = useState(null);
  const [arende, setArende] = useState(null);
  const [arendefel, setArendefel] = useState('');
  const fokusRefRef = useRef(null);
  const listSekvensRef = useRef(0);
  const valtRef = useRef(null);
  const dirtyRef = useRef(false);
  const openRef = useRef(false);
  const fokuseraFlikRef = useRef(false);
  const [hamtaNr, setHamtaNr] = useState(0);

  // Bara det senaste svaret används, så att ett långsamt äldre svar inte skriver över ett nyare.
  const laddaLista = useCallback(async () => {
    const nr = ++listSekvensRef.current;
    setListfel('');
    try {
      const data = await hamtaMinaArenden();
      if (nr !== listSekvensRef.current) return;
      setRader(data);
      setNya(nyStatus(data));
    } catch (err) {
      if (nr === listSekvensRef.current) setListfel(err.message || 'Försök igen om en stund.');
    }
  }, []);

  const laddaArende = useCallback(async (ref) => {
    setArende(null);
    setArendefel('');
    try {
      const data = await hamtaMittArende(ref);
      if (valtRef.current !== ref) return;                     // användaren har bytt ärende under tiden
      setArende(data);
      markeraSett(data);
      setNya((prev) => { const n = new Set(prev); n.delete(ref); return n; });
    } catch (err) {
      if (valtRef.current === ref) setArendefel(err.message || 'Försök igen om en stund.');
    }
  }, []);
  useEffect(() => { valtRef.current = valt; }, [valt]);

  const oppna = useCallback((forval) => {
    if (skickarRef.current) return;
    const f = forval || {};
    // Öppnas panelen igen medan den redan är öppen med ett påbörjat utkast behålls utkastet.
    if (!(openRef.current && dirtyRef.current)) {
      setKalla(KALLOR.includes(f.kalla) ? f.kalla : 'fab');
      setVarden(forvalTillVarden(f));
      setFiler([]); setFilfel([]); setFel({}); setSkickfel('');
      const ctx = getContext();
      autoModulRef.current = (ctx && ctx.module && ctx.module !== 'Övrigt') ? ctx.module : '';
    }
    setKlar(null);
    const mina = f.vy === 'mina';
    setFlik(mina ? 'mina' : 'nytt');
    setValt(mina && REF.test(f.arende || '') ? f.arende : null);
    if (!openRef.current) setRader(null);
    setHamtaNr((n) => n + 1);
    setOpen(true);
  }, [getContext]);
  useEffect(() => { openRef.current = open; }, [open]);

  // Listan hämtas varje gång panelen öppnas (räknaren i fliken) och när användaren byter till
  // fliken Mina ärenden (i flikens onChange), en gång per tillfälle.
  useEffect(() => { if (open) laddaLista(); }, [open, hamtaNr, laddaLista]);
  useEffect(() => { if (open && valt) laddaArende(valt); }, [open, valt, laddaArende]);

  // Flikbyte byter panelens läge (gråtoning bara på Nytt ärende), och då flyttar panelen fokus.
  // Lägg tillbaka fokus på den valda fliken, efter panelens egen fokusering.
  useEffect(() => {
    if (!fokuseraFlikRef.current) return undefined;
    fokuseraFlikRef.current = false;
    const t = window.setTimeout(() => {
      const el = document.querySelector('.forslag-panel .kp-panel__tab.is-active');
      if (el) el.focus({ preventScroll: true });
    }, 0);
    return () => window.clearTimeout(t);
  }, [flik]);

  const tillbaka = useCallback(() => {
    fokusRefRef.current = valt;
    setValt(null);
    setArende(null);
  }, [valt]);

  // Esc i ett ärende går tillbaka till listan. Fångas före panelens egen Esc (som stänger),
  // men bara när förslagspanelen är överst och fokus ligger i den: en meny, notislista, väljare
  // eller annan panel som ligger ovanpå får sitt eget Esc.
  useEffect(() => {
    if (!open || flik !== 'mina' || !valt) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      const panel = document.querySelector('.forslag-panel');
      const fokus = document.activeElement;
      const paneler = document.querySelectorAll('.kp-panel');            // i öppningsordning (portaler sist i body)
      if (!panel || paneler[paneler.length - 1] !== panel) return;
      if (fokus && fokus !== document.body && !panel.contains(fokus)) return;
      e.preventDefault();
      tillbaka();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [open, flik, valt, tillbaka]);

  // Tillbaka flyttar fokus till raden man kom från.
  useEffect(() => {
    if (valt || !fokusRefRef.current || !rader) return;
    const ref = fokusRefRef.current;
    fokusRefRef.current = null;
    window.setTimeout(() => {
      const el = document.querySelector(`.forslag-panel .mf-rad[data-ref="${ref}"]`);
      if (el) el.focus();
    }, 0);
  }, [valt, rader]);

  const foljArendet = () => { laddaLista(); setFlik('mina'); setValt(klar.id); setKlar(null); };

  const kastaUtkast = async () => {
    if (!dirtyRef.current) return true;
    return await kpConfirm({
      title: 'Kasta utkastet?',
      message: 'Du har ett påbörjat ärende som inte är skickat. Det försvinner om du fortsätter.',
      confirmLabel: 'Kasta utkastet',
      cancelLabel: 'Behåll',
      variant: 'warning',
    });
  };

  const foljdfraga = async () => {
    const ref = arende.ref;
    if (!(await kastaUtkast())) return;
    setVarden({ ...TOMT, typKey: 'question', beskrivning: `Följdfråga till ${ref}: ` });
    setFiler([]); setFilfel([]); setFel({});
    setValt(null); setArende(null); setKlar(null); setFlik('nytt');
  };

  // Länken till På gång stänger panelen; ett osparat utkast frågar först.
  const stangForLank = async () => {
    if (!(await kastaUtkast())) return false;
    setOpen(false);
    return true;
  };

  useEffect(() => {
    const nya = (e) => oppna(e.detail);
    const gamla = () => oppna({ kalla: 'fab' });
    window.addEventListener(OPPNA_FORSLAG, nya);
    window.addEventListener(OPPNA_FORSLAG_GAMMAL, gamla);
    return () => {
      window.removeEventListener(OPPNA_FORSLAG, nya);
      window.removeEventListener(OPPNA_FORSLAG_GAMMAL, gamla);
    };
  }, [oppna]);

  // Hörnknappen och KanAI-knappen ligger nere till höger och döljs medan panelen är öppen.
  useEffect(() => {
    document.body.classList.toggle('kp-forslag-open', open);
    document.body.classList.toggle('fb-panel-open', open);
    return () => {
      document.body.classList.remove('kp-forslag-open');
      document.body.classList.remove('fb-panel-open');
    };
  }, [open]);

  const satt = useCallback((falt, v) => {
    setVarden((prev) => ({ ...prev, [falt]: v }));
    if (falt === 'rubrik') setFel((prev) => ({ ...prev, rubrik: undefined }));
  }, []);

  const laggTill = useCallback((nya) => {
    setFiler((prev) => {
      const { ok, fel: f } = sorteraFiler(nya, prev.length);
      setFilfel(f);
      return [...prev, ...ok];
    });
  }, []);

  const taBort = useCallback((i) => { setFiler((prev) => prev.filter((_, j) => j !== i)); setFilfel([]); }, []);

  // Klistra in en skärmbild eller fil var som helst i panelen.
  const onPaste = (e) => {
    const items = Array.from((e.clipboardData && e.clipboardData.files) || []);
    if (!items.length) return;
    e.preventDefault();
    laggTill(items.map((f, i) => (f.name && f.name !== 'image.png' ? f : new File([f], `inklistrad-${Date.now()}-${i + 1}.${(f.type.split('/')[1] || 'png')}`, { type: f.type }))));
  };

  // Under skick stängs panelen inte (Esc, X, gråtoningen), annars försvinner referensen.
  const stang = () => { if (!skickarRef.current) setOpen(false); };

  const skicka = async () => {
    if (skickar) return;
    if (varden.rubrik.trim().length < 3) { setFel({ rubrik: 'Skriv en rubrik på minst 3 tecken.' }); return; }
    skickarRef.current = true;
    setSkickar(true);
    setSkickfel('');
    try {
      const ctx = getContext();
      const anrop = byggAnrop({ ...varden, modul: varden.modul || (kalla === 'pa_gang' ? '' : autoModulRef.current) }, ctx, kalla, await hamtaAppVersion(), filer.length);
      const res = await skapaForslag(anrop);
      const misslyckade = [];
      for (const fil of filer) {
        try { await laddaUppFil(res.id, fil); } catch (err) { misslyckade.push(err.message || fil.name); }
      }
      setKlar({ id: res.id, typKey: varden.typKey, misslyckade });
      push({ type: 'success', message: `Tack. Ditt ärende har referens ${res.id}.` });
      setFiler([]);
    } catch (err) {
      setSkickfel(err.message || 'Förslaget kunde inte skickas. Försök igen.');
    } finally {
      skickarRef.current = false;
      setSkickar(false);
    }
  };

  const t = typFor(varden.typKey);
  const dirty = !klar && !skickar && (varden.rubrik.trim() !== '' || varden.beskrivning.trim() !== '' || filer.length > 0);
  dirtyRef.current = dirty;
  const iArende = flik === 'mina' && !!valt;
  const antalAktiva = rader ? rader.filter(arAktivt).length : 0;

  const flikar = iArende ? null : (
    <KpPanelTabs
      ariaLabel="Support och förslag"
      value={flik}
      onChange={(v) => {
        if (v !== flik) fokuseraFlikRef.current = true;
        if (v === 'mina' && v !== flik) laddaLista();
        setFlik(v); setValt(null);
      }}
      tabs={[
        { value: 'nytt', label: 'Nytt ärende' },
        { value: 'mina', label: <>Mina ärenden{antalAktiva > 0 && <>{' '}<span className="mf-raknare">{antalAktiva}</span></>}</> },
      ]}
    />
  );

  let footer;
  if (flik === 'mina') {
    footer = iArende && arende && arende.typ === 'question' && arende.kommentar ? (
      <button type="button" className="forslag-btn forslag-btn--sek" onClick={foljdfraga}>
        <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M11.5 2.5a1.4 1.4 0 0 1 2 2L5 13l-3 1 1-3z" /></svg>
        Ställ en följdfråga
      </button>
    ) : (iArende ? null : <MejlVal />);
  } else if (klar) {
    footer = (
      <>
        <button type="button" className="forslag-btn forslag-btn--sek" onClick={foljArendet}>Följ ärendet</button>
        <button type="button" className="forslag-btn forslag-btn--primar" onClick={stang}>Stäng</button>
      </>
    );
  } else footer = ({ requestClose }) => (
    <>
      {skickfel && <span className="forslag-skickfel" role="alert">{skickfel}</span>}
      <button type="button" className="forslag-btn forslag-btn--sek" onClick={requestClose} disabled={skickar}>Avbryt</button>
      <button type="button" className="forslag-btn forslag-btn--primar" onClick={skicka} disabled={skickar}>
        {skickar ? 'Skickar...' : t.knapp}
      </button>
    </>
  );

  return (
    <KpPanel
      open={open}
      onClose={stang}
      size="md"
      mode={flik === 'nytt' && !klar ? 'form' : 'detail'}
      dirty={dirty}
      className="forslag-panel"
      context={flik === 'nytt' && !klar ? 'Support och förslag' : undefined}
      ariaLabel={iArende ? `Ärende ${valt}${arende ? `: ${arende.rubrik}` : ''}` : undefined}
      title={iArende ? (
        <>
          <button type="button" className="kp-icon-btn" aria-label="Tillbaka till Mina ärenden" onClick={tillbaka}><IkonPil vand className="mf-tillbaka__pil" /></button>
          <span className="mf-huvud">
            {arende && <span className="mf-huvud__ref">{`${arende.ref} · ${TYPNAMN[arende.typ] || 'Ärende'} · skickad ${datum(arende.skapad)}`}</span>}
            <span>{arende ? arende.rubrik : valt}</span>
          </span>
        </>
      ) : (flik === 'nytt' && !klar ? t.titel : 'Support och förslag')}
      tabs={flikar}
      footer={footer}
    >
      {flik === 'mina' ? (
        <div className={iArende ? undefined : 'mf-platt'}>
          {iArende ? (
            <MinaArendenDetalj arende={arende} fel={arendefel} onForsok={() => laddaArende(valt)} onStang={stangForLank} />
          ) : (
            <MinaArendenLista rader={rader} fel={listfel} nya={nya} onOppna={setValt} onForsok={laddaLista} onNytt={() => setFlik('nytt')} />
          )}
        </div>
      ) : (
      <div onPaste={klar ? undefined : onPaste}>
        {klar ? (
          <div className="forslag-klar" role="status">
            <div className="forslag-klar__ikon" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" />
              </svg>
            </div>
            <p className="forslag-klar__text">Tack. Ditt ärende har referens</p>
            <p className="forslag-klar__ref">{klar.id}</p>
            <p className="forslag-klar__sub">Du får ett mejl nu och när ärendet har registrerats. Systemansvarig går igenom alla inskick och lägger in dem i planeringen. Du kan följa ärendet under Mina ärenden.</p>
            {klar.misslyckade.length > 0 && (
              <ul className="forslag-filer__fel" role="alert">
                {klar.misslyckade.map((m) => <li key={m}>{m}</li>)}
              </ul>
            )}
          </div>
        ) : (
          <ForslagFormular
            varden={varden}
            satt={satt}
            filer={filer}
            filfel={filfel}
            onLaggTillFiler={laggTill}
            onTaBortFil={taBort}
            fel={fel}
            autoModul={kalla === 'pa_gang' ? '' : autoModulRef.current}
          />
        )}
      </div>
      )}
    </KpPanel>
  );
}
